import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { registerHelp, unregisterHelp, isHelpFetched, requestHelp } from '../services/helpCache';
import { useHelpEntry, HOVER_INTENT_MS } from '../services/useHelp';

import { emoText } from '../shell/EmojiIcons';
const POPOVER_W = 300;

// A localized, display-only counterpart to HelpIcon for copy that already lives in a
// page's i18n namespace (rather than in the database-backed help catalogue). It uses
// the same visible affordance and portal card as HelpIcon, but opens on hover/focus.
export function TextTooltip({ text, ariaLabel, glyph = 'i' }) {
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);

  // Register with the page Guide (keyed by the text) so a plain-text tooltip is listed there like a DB help icon.
  const textKey = useMemo(() => (text ? `tt.${String(text).slice(0, 60)}.${String(text).length}` : null), [text]);
  useEffect(() => {
    if (!textKey) return undefined;
    registerHelp(textKey, { label: ariaLabel && ariaLabel !== text ? ariaLabel : null, text });
    return () => unregisterHelp(textKey);
  }, [textKey, text, ariaLabel]);

  const open = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.max(8, Math.min(r.left + r.width / 2 - POPOVER_W / 2, window.innerWidth - POPOVER_W - 8));
    const spaceBelow = window.innerHeight - r.bottom - 14;
    const spaceAbove = r.top - 14;
    setPos(spaceBelow >= 150 || spaceBelow >= spaceAbove
      ? { left, top: r.bottom + 6, maxH: spaceBelow }
      : { left, bottom: window.innerHeight - r.top + 6, maxH: spaceAbove });
  };
  const close = () => setPos(null);

  return (
    <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 3 }} onMouseEnter={open} onMouseLeave={close}>
      <button ref={btnRef} type="button" aria-label={ariaLabel || text} onFocus={open} onBlur={close}
        onClick={e => { e.preventDefault(); e.stopPropagation(); open(); }} onKeyDown={e => { if (e.key === 'Escape') close(); }} className="help-icon" style={iconBtn}>{glyph}</button>
      {pos && createPortal(
        <div role="tooltip" style={{ ...popover, pointerEvents: 'none', left: pos.left, ...(pos.top != null ? { top: pos.top } : { bottom: pos.bottom }), maxHeight: Math.max(120, pos.maxH), overflowY: 'auto' }}>
          <span style={{ display: 'block', fontSize: 12, lineHeight: 1.55, color: '#334155', overflowWrap: 'break-word' }}>{text}</span>
        </div>,
        document.body
      )}
    </span>
  );
}

// GES-323: optional `whereUsed` = [[label, text], ...] renders ONE merged tooltip — the DB help entry
// first (when one exists), then a 'Where used' block. The merged variant opens on hover/focus (no overlay).
export default function HelpIcon({ helpKey, whereUsed = null, label = null }) {
  // 41136 (GES-253): only the "Help:" PREFIX is ours. `entry.title` / `entry.body` come from the
  // help catalog in the database — data, not UI copy, and out of scope for a web-only change.
  const { t, i18n } = useTranslation();
  // 47107 A7k: the row is read on demand. 42079 locale rule kept: the active language's `<key>__<lang>` row, else the base key.
  // Nothing is requested until the user shows intent: the pointer rests HOVER_INTENT_MS on the icon, or it is focused / clicked.
  const lang = (i18n.language || 'en').slice(0, 2);
  const [wanted, setWanted] = useState(false);
  const { entry, loading } = useHelpEntry(helpKey, lang, wanted);
  const [pos, setPos] = useState(null); // {top,left} in viewport coords, or null=closed
  const btnRef = useRef(null);
  const intent = useRef(null);
  const ask = () => { setWanted(true); requestHelp([helpKey], lang); };   // idempotent: a read that failed or was aborted is asked again on the next intent
  const startIntent = () => { if (!intent.current) intent.current = setTimeout(() => { intent.current = null; ask(); }, HOVER_INTENT_MS); };
  const stopIntent = () => { clearTimeout(intent.current); intent.current = null; };
  useEffect(() => stopIntent, []);

  // v0.43: register this key while mounted so the page Guide can aggregate every ⓘ on the
  // current tab (tab-aware — icons mount/unmount with their tab).
  // `whereUsed` is rebuilt on every render by callers, so register on its content, not its identity.
  const extraKey = whereUsed ? JSON.stringify(whereUsed) : '';
  useEffect(() => {
    registerHelp(helpKey, whereUsed ? { label, whereUsed } : undefined);
    return () => unregisterHelp(helpKey);
  }, [helpKey, label, extraKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!entry && !whereUsed && isHelpFetched(helpKey, lang)) return null;   // the catalogue has no row for this key: no icon (known after the first read)
  const merged = !!whereUsed;
  const title = entry?.title || label || (whereUsed ? 'Where used' : t('stgUi.help'));

  // Fixed-position popover anchored under the button and clamped to the viewport,
  // so it is never clipped by a scrolling/overflow-hidden container (e.g. the
  // Work Queue panel).
  function toggle(e) {
    e.stopPropagation();
    ask();
    if (pos && !merged) { setPos(null); return; }
    const r = btnRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(r.left + r.width / 2 - POPOVER_W / 2, window.innerWidth - POPOVER_W - 8));
    // Open below by default; flip above when there isn't enough room, and cap the height to the
    // available space (the body scrolls) so a long entry never spills off the viewport.
    const spaceBelow = window.innerHeight - r.bottom - 14;
    const spaceAbove = r.top - 14;
    if (spaceBelow >= 150 || spaceBelow >= spaceAbove) {
      setPos({ left, top: r.bottom + 6, maxH: spaceBelow });
    } else {
      setPos({ left, bottom: window.innerHeight - r.top + 6, maxH: spaceAbove });
    }
  }

  return (
    <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 3 }}
      onMouseEnter={e => { startIntent(); if (merged) toggle(e); }} onMouseLeave={() => { stopIntent(); if (merged) setPos(null); }}>
      <button ref={btnRef} type="button" onClick={toggle} onFocus={merged ? toggle : ask} onBlur={merged ? () => setPos(null) : undefined} className="help-icon" style={iconBtn} aria-label={t('shared.help.ariaLabel', { title })} {...(merged || !entry ? {} : { title })}>i</button>
      {/* Portal to body: the icon often sits inside a <label> (the dismiss overlay
          would toggle the labelled checkbox) or a nowrap header — rendering at the
          body detaches the popover from any anchor context. */}
      {pos && createPortal(
        <>
          {!merged && <div onClick={(e) => { e.preventDefault(); setPos(null); }} style={overlay} />}
          <div role={merged ? 'tooltip' : undefined} style={{ ...popover, ...(merged ? { pointerEvents: 'none' } : {}), left: pos.left, ...(pos.top != null ? { top: pos.top } : { bottom: pos.bottom }), maxHeight: Math.max(120, pos.maxH), overflowY: 'auto' }}>
            {!entry && !merged && loading && <span data-testid="help-skeleton" aria-busy="true" style={skeleton} />}
            {entry && <strong style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#0f172a' }}>{entry.title}</strong>}
            {!entry && label && <strong style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#0f172a' }}>{emoText(label)}</strong>}
            {entry && <span style={{ display: 'block', fontSize: 12, lineHeight: 1.55, color: '#334155', overflowWrap: 'break-word' }}>{entry.body}</span>}
            {entry?.formula && (
              <code style={formulaStyle}>{entry.formula}</code>
            )}
            {entry?.learn_more && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>{entry.learn_more}</div>
            )}
            {whereUsed && (
              <div style={entry ? { marginTop: 10, paddingTop: 8, borderTop: '1px solid #e2e8f0' } : undefined}>
                <strong style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#0f172a' }}>Where used</strong>
                {whereUsed.map(([label, text]) => (
                  <div key={label} style={{ fontSize: 12, lineHeight: 1.5, color: '#334155', overflowWrap: 'break-word', marginBottom: 3 }}><strong>{emoText(label)}:</strong> {text}</div>
                ))}
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </span>
  );
}

const iconBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 15, height: 15, borderRadius: '50%',
  background: '#3b82f6', color: 'white',
  border: '1px solid transparent', cursor: 'pointer',
  fontSize: 10, fontWeight: 700, fontStyle: 'italic',
  lineHeight: 1, padding: 0, flexShrink: 0,
};

const skeleton = { display: 'block', height: 38, borderRadius: 4, background: 'linear-gradient(90deg,#f1f5f9,#e2e8f0,#f1f5f9)' };

const overlay = {
  position: 'fixed', inset: 0, zIndex: 999,
};

const popover = {
  position: 'fixed', zIndex: 1000,
  width: POPOVER_W, maxWidth: 'calc(100vw - 16px)', boxSizing: 'border-box',
  fontWeight: 400, // don't inherit bold from a bold header (e.g. Work Queue TASKS)
  whiteSpace: 'normal', // don't inherit nowrap from anchors like table headers or nowrap labels — the body must wrap inside the card
  fontStyle: 'normal',
  background: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: '12px 14px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
  textAlign: 'left',
};

const formulaStyle = {
  display: 'block', marginTop: 8,
  background: '#f1f5f9', borderRadius: 4,
  padding: '5px 8px', fontFamily: 'monospace',
  fontSize: 11, color: '#1e293b', whiteSpace: 'pre-wrap',
};
